import { memo, useMemo } from 'react';
import type {
  CompletedDrivingTelemetryRun,
  DrivingTelemetrySample,
} from '../model/drivingTelemetry';

const WIDTH = 320;
const HEIGHT = 112;
const LEFT = 8;
const RIGHT = WIDTH - 8;

function linePath(
  samples: readonly DrivingTelemetrySample[],
  duration: number,
  yOf: (sample: DrivingTelemetrySample) => number
): string {
  return samples
    .map((sample, index) => {
      const x = LEFT + (Math.min(duration, sample.timeSeconds) / Math.max(0.001, duration)) * (RIGHT - LEFT);
      const y = yOf(sample);
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

function DrivingTelemetryChartView({ run }: { run: CompletedDrivingTelemetryRun }) {
  const chart = useMemo(() => {
    const samples = run.samples;
    const maxSpeed = Math.max(1, ...samples.map((sample) => sample.speedKmh));
    const maxLateralG = Math.max(0, ...samples.map((sample) => Math.abs(sample.lateralG)));
    const averageSpeed =
      samples.length === 0
        ? 0
        : samples.reduce((sum, sample) => sum + sample.speedKmh, 0) / samples.length;
    const speedCeiling = Math.max(100, Math.ceil(maxSpeed / 50) * 50);
    const speed = linePath(
      samples,
      run.durationSeconds,
      (sample) => 61 - (Math.min(speedCeiling, sample.speedKmh) / speedCeiling) * 49
    );
    const throttle = linePath(
      samples,
      run.durationSeconds,
      (sample) => 101 - Math.max(0, sample.throttle) * 22
    );
    const brake = linePath(
      samples,
      run.durationSeconds,
      (sample) => 101 - sample.brake * 22
    );
    const lateralGScale = Math.max(0.5, Math.ceil(maxLateralG * 2) / 2);
    const lateralG = linePath(
      samples,
      run.durationSeconds,
      (sample) => 72 - (sample.lateralG / lateralGScale) * 8
    );
    return { maxSpeed, averageSpeed, maxLateralG, speedCeiling, speed, throttle, brake, lateralG };
  }, [run]);

  return (
    <section className="driving-telemetry-chart" aria-label="최근 완료 주행 분석">
      <div className="driving-telemetry-chart__head">
        <strong>{run.kind === 'course' ? '최근 코스 텔레메트리' : '최근 챌린지 텔레메트리'}</strong>
        <span>{run.durationSeconds.toFixed(1)}초 · {run.samples.length} 샘플</span>
      </div>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} aria-hidden="true">
        <line x1={LEFT} y1="61" x2={RIGHT} y2="61" className="driving-telemetry-chart__grid" />
        <line x1={LEFT} y1="72" x2={RIGHT} y2="72" className="driving-telemetry-chart__grid" />
        <line x1={LEFT} y1="101" x2={RIGHT} y2="101" className="driving-telemetry-chart__grid" />
        <text x={LEFT} y="9">{chart.speedCeiling} km/h</text>
        <path d={chart.speed} className="driving-telemetry-chart__speed" />
        <path d={chart.lateralG} className="driving-telemetry-chart__lateral" />
        <path d={chart.throttle} className="driving-telemetry-chart__throttle" />
        <path d={chart.brake} className="driving-telemetry-chart__brake" />
      </svg>
      <div className="driving-telemetry-chart__legend" aria-hidden="true">
        <span data-series="speed">속도</span>
        <span data-series="lateral">횡G</span>
        <span data-series="throttle">가속</span>
        <span data-series="brake">제동</span>
      </div>
      <p>
        최고 {Math.round(chart.maxSpeed)} km/h · 평균 {Math.round(chart.averageSpeed)} km/h ·
        최대 횡G {chart.maxLateralG.toFixed(2)}
      </p>
    </section>
  );
}

/** 완료된 run 객체가 바뀔 때만 SVG 경로를 다시 계산합니다. */
export const DrivingTelemetryChart = memo(DrivingTelemetryChartView);
